import React, { useState } from 'react';
import { Plus, Eye, Edit2, Trash2, UserPlus } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDate, generateId } from '../../utils/helpers';

const emptyForm = { firstName:'', lastName:'', dob:'', gender:'boy', bloodGroup:'A+', phone:'', email:'', address:'', classId:'', sectionId:'A', rollNumber:'', admissionDate: new Date().toISOString().split('T')[0], status:'active', parentId:'' };

function StudentList() {
  const { students, setStudents, classes, parents, feeStructures, feePayments } = useApp();
  const { currentUser } = useAuth();
  const isTeacher = currentUser?.role === 'teacher';

  // Classes assigned to this teacher (as class teacher)
  const teacherClasses = isTeacher
    ? classes.filter(c => c.classTeacherId === currentUser?.profileId)
    : classes;

  // Base student pool scoped to teacher's classes
  const baseStudents = isTeacher
    ? students.filter(s => teacherClasses.some(c => c.id === s.classId))
    : students;

  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'view'
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);
  const [filterClass, setFilterClass] = useState('all');

  const openAdd = () => { setForm({...emptyForm, admissionNumber: `ADM${Date.now()}`}); setModal('add'); };
  const openEdit = (s) => { setForm(s); setSelected(s); setModal('edit'); };
  const openView = (s) => { setSelected(s); setModal('view'); };

  const save = () => {
    if (modal === 'add') {
      setStudents(prev => [...prev, { ...form, id: generateId('STU') }]);
    } else {
      setStudents(prev => prev.map(s => s.id === selected.id ? form : s));
    }
    setModal(null);
  };

  const remove = (id) => {
    if (window.confirm('Delete this student?')) setStudents(prev => prev.filter(s => s.id !== id));
  };

  const getClass = (s) => { const c = classes.find(c => c.id === s.classId); return c ? `${c.name}-${s.sectionId}` : '-'; };
  const getParent = (s) => { const p = parents.find(p => p.id === s.parentId); return p ? p.name : '-'; };
  const getFeeBalance = (s) => {
    const structure = feeStructures.find(f => f.classId === s.classId);
    if (!structure) return null;
    const totalDue = structure.totalAmount + (s.previousYearBalance || 0);
    const paid = feePayments.filter(p => p.studentId === s.id).reduce((sum, p) => sum + (p.amount || 0), 0);
    return totalDue - paid;
  };

  const columns = [
    { label: '#', key: 'num', render: (_, i) => i+1 },
    { label: 'Student', key: 'name', render: s => (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold flex-shrink-0">
          {s.firstName?.[0]}{s.lastName?.[0]}
        </div>
        <div><p className="font-medium">{s.firstName} {s.lastName}</p><p className="">{s.admissionNumber}</p></div>
      </div>
    ), sortable: false },
    { label: 'Class', key: 'class', render: s => getClass(s) },
    { label: 'Gender', accessor: 'gender', sortable: true },
    { label: 'Parent', key: 'parent', render: s => getParent(s) },
    { label: 'Phone', accessor: 'phone' },
    { label: 'Fee Balance', key: 'fee', render: s => {
      const bal = getFeeBalance(s);
      if (bal === null) return <span className="text-gray-400">—</span>;
      if (bal <= 0) return <span className="text-green-600 font-semibold">Cleared</span>;
      return <span className="text-red-600 font-semibold">₹{bal.toLocaleString('en-IN')}</span>;
    }},
    { label: 'Status', key: 'status', render: s => <Badge value={s.status} /> },
  ];

  const fields = (
    <div className="grid grid-cols-2 gap-4">
      {[['First Name','firstName','text'],['Last Name','lastName','text'],['Date of Birth','dob','date'],['Phone','phone','tel'],['Email','email','email'],['Roll Number','rollNumber','text'],['Admission Date','admissionDate','date'],['Address','address','text']].map(([label,key,type]) => (
        <div key={key} className={key === 'address' ? 'col-span-2' : ''}>
          <label className="block text-sm font-medium mb-1">{label}</label>
          <input type={type} value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
            className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
      ))}
      {[['Gender','gender',['boy','girl']],['Blood Group','bloodGroup',['A+','A-','B+','B-','O+','O-','AB+','AB-']],['Status','status',['active','inactive','transferred','graduated']]].map(([label,key,opts]) => (
        <div key={key}>
          <label className="block text-sm font-medium mb-1">{label}</label>
          <select value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
            className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            {opts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      ))}
      <div>
        <label className="block text-sm font-medium mb-1">Class</label>
        <select value={form.classId||''} onChange={e=>setForm(f=>({...f,classId:e.target.value}))}
          className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          <option value="">Select Class</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name} - Sec {c.section}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Parent</label>
        <select value={form.parentId||''} onChange={e=>setForm(f=>({...f,parentId:e.target.value}))}
          className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          <option value="">Select Parent</option>
          {parents.map(p => <option key={p.id} value={p.id}>{p.name} ({p.relation})</option>)}
        </select>
      </div>
    </div>
  );

  const filteredStudents = filterClass === 'all' ? baseStudents : baseStudents.filter(s => s.classId === filterClass);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Students</h1>
          <p className="text-gray-500 text-sm mt-0.5">{filteredStudents.length} of {students.length} students</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="all">All Classes</option>
            {teacherClasses.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
          </select>
          {!isTeacher && (
            <button onClick={openAdd} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded-lg text-sm font-medium transition-colors">
              <Plus size={16} /> Add Student
            </button>
          )}
        </div>
      </div>
      <DataTable columns={columns} data={filteredStudents}
        actions={row => <>
          <button onClick={()=>openView(row)} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600" title="View"><Eye size={15}/></button>
          {!isTeacher && <>
            <button onClick={()=>openEdit(row)} className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-600" title="Edit"><Edit2 size={15}/></button>
            <button onClick={()=>remove(row.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500" title="Delete"><Trash2 size={15}/></button>
          </>}
        </>}
      />
      {/* Add/Edit Modal */}
      <Modal isOpen={modal==='add'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='add'?'New Student Admission':'Edit Student'} size="xl">
        {fields}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={()=>setModal(null)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
        </div>
      </Modal>
      {/* View Modal */}
      <Modal isOpen={modal==='view'} onClose={()=>setModal(null)} title="Student Profile" size="lg">
        {selected && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-2xl font-bold">
                {selected.firstName?.[0]}{selected.lastName?.[0]}
              </div>
              <div>
                <h3 className="text-xl font-bold">{selected.firstName} {selected.lastName}</h3>
                <p className="text-gray-500">{selected.admissionNumber}</p>
                <Badge value={selected.status} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
              {[['Class', getClass(selected)], ['Roll No', selected.rollNumber], ['DOB', formatDate(selected.dob)], ['Gender', selected.gender], ['Blood Group', selected.bloodGroup], ['Phone', selected.phone], ['Email', selected.email], ['Parent', getParent(selected)], ['Admission Date', formatDate(selected.admissionDate)], ['Address', selected.address]].map(([k,v]) => (
                <div key={k}><p className="font-medium">{k}</p><p className="text-sm font-medium">{v||'-'}</p></div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
export default StudentList;
